import { useState, useCallback, useEffect } from 'react';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface QuickBooksPurchaseOrder {
  purchasePrice: number;
  purchaseOrderNumber: string;
  purchaseOrderDate: string;
  invoiceNumber: string;
  customFieldNERV: string;
}

export function useQuickBooks() {
  const [isLoading, setIsLoading] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<QuickBooksPurchaseOrder[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);

  const QUICKBOOKS_COMPANY_ID = '9341455361959454';

  // Check connection status using backend status endpoint
  const checkConnectionStatus = useCallback(async () => {
    try {
      const { data, error } = await invokeFunction(supabase, 'quickbooks-oauth', {
        body: { 
          action: 'status',
          companyId: QUICKBOOKS_COMPANY_ID
        }
      });

      if (error) {
        setIsConnected(false);
        return false;
      }

      const isConnected = data.connected === true;
      setIsConnected(isConnected);
      
      // If connection failed and requires reauth, show appropriate message
      if (!isConnected && data.requiresReauth) {
        toast.error('QuickBooks connection expired. Please reconnect.');
      }
      
      return isConnected;
    } catch (error) {
      console.error('Error checking QuickBooks connection status:', error);
      setIsConnected(false);
      return false;
    }
  }, [QUICKBOOKS_COMPANY_ID]);

  const connectToQuickBooks = useCallback(async () => {
    try {
      const { data, error } = await invokeFunction(supabase, 'quickbooks-oauth', {
        body: { action: 'auth' }
      });
      
      if (error) throw error;
      
      // Only set connecting state after we successfully get the auth URL
      setIsConnecting(true);
      
      // Open QuickBooks OAuth in a popup
      const popup = window.open(
        data.authUrl,
        'quickbooks-auth',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );
      
      // Handle popup blocked or failed to open
      if (!popup) {
        setIsConnecting(false);
        toast.error('Popup blocked. Please allow popups and try again.');
        return;
      }
      
      // Poll for popup closure
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          setIsConnecting(false);
          // Check connection status and fetch purchase orders after connection
          setTimeout(async () => {
            const connected = await checkConnectionStatus();
            if (connected) {
              fetchPurchaseOrders();
            }
          }, 1000);
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error connecting to QuickBooks:', error);
      toast.error('Failed to connect to QuickBooks');
      setIsConnecting(false);
    }
  }, []);

  const fetchPurchaseOrders = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await invokeFunction(supabase, 'quickbooks-oauth', {
        body: { 
          action: 'fetch-purchase-orders',
          companyId: QUICKBOOKS_COMPANY_ID
        }
      });

      if (error) throw error;

      setPurchaseOrders(data.purchaseOrders || []);
      toast.success(`Loaded ${data.purchaseOrders?.length || 0} purchase orders`);
      
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      const errorMessage = error.message || 'Failed to fetch purchase orders from QuickBooks';
      
      if (errorMessage.includes('not connected')) {
        toast.error('Please connect to QuickBooks first');
      } else if (errorMessage.includes('Forbidden')) {
        toast.error('QuickBooks access denied. Please reconnect with proper permissions.');
      } else {
        toast.error(errorMessage);
      }
      setPurchaseOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check connection status on component mount
  useEffect(() => {
    const initializeConnection = async () => {
      const connected = await checkConnectionStatus();
      if (connected) {
        fetchPurchaseOrders();
      }
      setIsCheckingConnection(false);
    };
    initializeConnection();
  }, [checkConnectionStatus, fetchPurchaseOrders]);

  return {
    isLoading,
    purchaseOrders,
    fetchPurchaseOrders,
    connectToQuickBooks,
    isConnected,
    isConnecting,
    isCheckingConnection,
  };
}